import "./ChartSurfaceDark.css";
import "./ChartFilterDark.css";
import "./Linechart.css";
import "./ChartCard.css";
import React, { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Label,
  ResponsiveContainer,
  Brush,
  ReferenceArea,
} from "recharts";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { Button, IconButton, Menu, TextField } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs';
import Autocomplete from '@mui/material/Autocomplete';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { getProducts } from "../../../actions/userActions/productAction";
import { getCallAveragEmployee } from "../../../actions/userActions/callAverageEmp";
import { Tooltip as MaterialTooltip } from "@mui/material";
import ApplyButton from "../buttons/ApplyButton";
import ResetButton from "../buttons/ResetButton";
import BodyMediumRegular from "../../designSystem/BodyMediumRegular";
import { User, CalendarDays, Package, Info, ActivitySquare, Search, X } from "lucide-react";
import SingleSelect from "../../designSystem/SingleSelect";
import MultiSelect from "../../designSystem/MultiSelect";
import BodySmallRegular from "../../designSystem/BodySmallRegular";
import LineChartTooltip from "./LineChartTooltip";
import LineChartLegend from "./LineChartLegend";
import GranularitySelect from "./GranularitySelect";
import { getSeriesColor, getSeriesDash } from "./chartPalette";
import {
  DATE_KEY,
  EMPTY_SUMMARY,
  summarise,
  rankSeries,
  bucketByTime,
} from "./chartData";
import useIsDarkTheme from "../../../hooks/useIsDarkTheme";
import { getSubOrdinates } from "../../../actions/userActions/tasks/taskAction";
import { t } from "../../../locales/translate";


export function Linechart({ chartSummaryData, loading, onChangeCallEmployee }) {
  const buttonLabels = {
    "today": "Today",
    "this week": "Week",
    "this month": "Month",
    "this year": "Year",
    "custom": `${t("custom")}`,
  };
  const employeeName = chartSummaryData.length > 0 ? chartSummaryData[0].employeeName : null;
  const [appliedFilters, setAppliedFilters] = useState([]);
  const currentDate = moment();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [customDate, setCustomDate] = useState(false);
  const employees = useSelector((state) => state.tasks_reducer.subOrdinates);
  const user = JSON.parse(localStorage.getItem("user"));
  const employeeId =
    user.result[0] !== undefined ? user.result[0].user.id : false;
  const dispatch = useDispatch();
  const [uniqueProducts, setUniqueProducts] = useState([]);
  const callAvgEmployee = useSelector(
    (state) => state.call_avg_emp_reducer.callAvgEmp
  );
  const [formError, setFormError] = useState({});
  function choosefilters(filters) {
    setAppliedFilters(filters);
  }
  const [Filters, setFilters] = useState({
    productIds: [],
    employee: 0,
    fromDate: moment(currentDate).subtract(7, "days").format("YYYY-MM-DD"),
    toDate: moment(currentDate).format("YYYY-MM-DD"),
  });
  const [selected, setSelected] = useState({
    productIds: [],
    employee: "",
    fromDate: moment(currentDate).subtract(7, "days").format("YYYY-MM-DD"),
    toDate: moment(currentDate).format("YYYY-MM-DD"),
  });
  const [productsName, setProductsName] = useState([]);
  const products = useSelector((state) => state.products_reducer.products);
  const isDark = useIsDarkTheme();
  /* Products the reader has clicked off in the legend, so a crowded chart can
     be read one line at a time. Purely a view concern - the data is untouched. */
  const [hiddenSeries, setHiddenSeries] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  /* "auto" | "day" | "week" | "month" | "year" - the resolution the points
     are plotted at. "auto" sizes it to the range; anything else is the
     reader's explicit choice from the granularity control. */
  const [granularity, setGranularity] = useState("auto");
  /* The visible window, as indices into the plotted rows. null means "show
     everything". The brush below the plot and drag-to-zoom both write here,
     so there is one source of truth for what is on screen. */
  const [zoom, setZoom] = useState(null);
  /* The in-progress drag selection, kept apart from `zoom` so an abandoned
     drag never changes what is shown. */
  const [dragSelection, setDragSelection] = useState(null);
  /* The date preset behind the chart on screen. Picking custom dates has to
     say so on the card, otherwise the range chip looks like one of the
     presets. */
  const [appliedPreset, setAppliedPreset] = useState("this week");
  /* The employee behind the chart on screen, as opposed to the one half-picked
     in the filter panel - the dot on the filter button reports this one. */
  const [appliedEmployee, setAppliedEmployee] = useState(0);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [cachedProductOptions, setCachedProductOptions] = useState([]);

  useEffect(() => {
    if (products && products !== "isLoading" && products.result?.length) {
      setCachedProductOptions(
        products.result
          .filter((product) => product.active)
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((product) => ({
            value: product.id,
            label: product.name,
          }))
      );
    }
  }, [products]);

  const setProductNames = (array) => {
    setProductsName(array);
  };
  useEffect(() => {
    let data = [];
    products?.result &&
      products !== "isLoading" &&
      products.result?.map((row) => {
        appliedFilters.map((row2) => {
          if (row.id == row2) {
            data.push(row.name);
          }
        });
      });
    setProductNames(data);
  }, [products, appliedFilters]);

  /* One row per day the API reported, before any bucketing. */
  const [dailyRows, setDailyRows] = useState([]);

  useEffect(() => {
    const sourceData =
      callAvgEmployee &&
        callAvgEmployee !== "isLoading" &&
        callAvgEmployee.byEmployee &&
        callAvgEmployee.byEmployee.length > 0
        ? callAvgEmployee.byEmployee
        : chartSummaryData || [];

    if (sourceData.length > 0) {
      const uniqueProducts = [];
      const groupedData = sourceData.reduce((result, item) => {
        const date = moment(item.dateTime).format("D MMM, YY");
        const key = `${date}`;

        if (!result[key]) {
          result[key] = { date };
        }

        if (!uniqueProducts.includes(item.productName)) {
          uniqueProducts.push(item.productName);
        }

        if (!result[key][item.productName]) {
          result[key][item.productName] = item.calls;
        } else {
          result[key][item.productName] += item.calls;
        }

        return result;
      }, {});

      const productNames = new Set();
      Object.values(groupedData).forEach((item) => {
        Object.keys(item).forEach((key) => {
          if (key !== "date") {
            productNames.add(key);
          }
        });
      });

      const outputData = Object.values(groupedData)
        .map((item) => {
          const formattedItem = {
            date: item.date,
          };
          productNames.forEach((productName) => {
            formattedItem[productName] = item[productName] || 0;
          });
          return formattedItem;
        })
        .sort((a, b) => moment(a.date, DATE_KEY).toDate() - moment(b.date, DATE_KEY).toDate());

      const allProducts = Array.from(productNames);
      /* Summarise the raw days first - the strip should report the employee,
         not whatever resolution the chart ends up drawn at. */
      setSummary(summarise(outputData, allProducts));

      /* Every product keeps its own line - no "Other" bucket. Past the eight
         palette slots the colours repeat with a stroke dash, so two products
         that share a hue are still told apart. */
      setDailyRows(outputData);
      setUniqueProducts(rankSeries(outputData, allProducts));
    } else {
      /* A filter that matches nothing used to leave the previous employee's
         chart on screen, which read as if the filter had done nothing. */
      setDailyRows([]);
      setUniqueProducts([]);
      setSummary(EMPTY_SUMMARY);
    }
  }, [callAvgEmployee, chartSummaryData]);

  /* Bucketing is kept out of the fetch effect so switching granularity
     redraws from the rows already in hand instead of going back to the API. */
  const { rows: data, unit: bucketUnit } = useMemo(
    () =>
      bucketByTime(
        dailyRows,
        uniqueProducts,
        granularity === "auto" ? null : granularity
      ),
    [dailyRows, uniqueProducts, granularity]
  );

  /* Colours come from the validated series palette and are keyed on the
     product name, so a product looks the same on every reload and after a
     filter changes how many lines are on screen. */
  const productColors = useMemo(
    () =>
      uniqueProducts.reduce((map, product) => {
        map[product] = getSeriesColor(product, isDark, "products");
        return map;
      }, {}),
    [uniqueProducts, isDark]
  );

  const toggleSeries = (product) => {
    setHiddenSeries((prev) =>
      prev.includes(product)
        ? prev.filter((item) => item !== product)
        : [...prev, product]
    );
  };

  /* Drop hidden products that are no longer part of the result set. */
  useEffect(() => {
    setHiddenSeries((prev) =>
      prev.filter((product) => uniqueProducts.includes(product))
    );
  }, [uniqueProducts]);

  /* Taken from the data that came back, not from the filter panel: the panel
     holds what the user is typing, which is not necessarily what is drawn. */
  const rangeLabel = summary.rangeLabel;

  const productScopeLabel = appliedFilters.length
    ? `${appliedFilters.length} ${t("products")}`
    : t("allProducts");

  const hasActiveFilters =
    appliedFilters.length > 0 || Boolean(appliedEmployee);

  const shownEmployeeName =
    (callAvgEmployee &&
      callAvgEmployee !== "isLoading" &&
      callAvgEmployee.byEmployee?.[0]?.employeeName) ||
    employeeName;

  const isChartLoading = callAvgEmployee === "isLoading" || loading;
  const hasPlottedData = !isChartLoading && data.length > 0;
  /* Says which resolution the points are plotted at, so nobody reads a month
     as a day - and lets the reader change it. */
  const GRANULARITY_OPTIONS = [
    { value: "auto", label: t("auto") },
    { value: "day", label: t("daily") },
    { value: "week", label: t("weekly") },
    { value: "month", label: t("monthly") },
    { value: "year", label: t("yearly") },
  ];
  const unitLabels = {
    day: t("daily"),
    week: t("weekly"),
    month: t("monthly"),
    year: t("yearly"),
  };
  const granularityLabel = unitLabels[bucketUnit] || t("daily");

  /* A new result set or a new resolution means the old window no longer
     refers to anything - start from the whole range again. */
  useEffect(() => {
    /* Bail out when there is nothing to reset, so a granularity change
       costs one render instead of two. */
    setZoom((prev) => (prev === null ? prev : null));
    setDragSelection((prev) => (prev === null ? prev : null));
  }, [dailyRows, granularity]);

  /* A zoom window is a pair of indices, which only mean anything against the
     exact rows they were taken from. Months 5-10 are a valid pair for 547
     daily rows too - they just point at six days in March instead - so
     picking Daily kept showing a sliver of the range, which read as the
     control not working. Bounds-checking does not catch that; the window has
     to carry the resolution and row count it belongs to, and is ignored the
     moment either changes. */
  const zoomWindow =
    zoom && zoom.unit === bucketUnit && zoom.length === data.length
      ? zoom
      : null;

  const windowFor = (startIndex, endIndex) => ({
    startIndex,
    endIndex,
    unit: bucketUnit,
    length: data.length,
  });

  const isZoomed =
    zoomWindow !== null &&
    (zoomWindow.startIndex > 0 || zoomWindow.endIndex < data.length - 1);

  const handleZoomStart = (event) => {
    if (!event || event.activeTooltipIndex == null) return;
    setDragSelection({
      startIndex: event.activeTooltipIndex,
      endIndex: event.activeTooltipIndex,
      left: event.activeLabel,
      right: event.activeLabel,
    });
  };

  const handleZoomMove = (event) => {
    if (!dragSelection || !event || event.activeTooltipIndex == null) return;
    /* A mouse move fires far more often than the selection actually changes.
       Re-rendering the whole plot on every pixel is what made dragging feel
       heavy, so nothing happens until the pointer crosses into a new
       bucket. */
    if (event.activeTooltipIndex === dragSelection.endIndex) return;
    setDragSelection((prev) => ({
      ...prev,
      endIndex: event.activeTooltipIndex,
      right: event.activeLabel,
    }));
  };

  const handleZoomEnd = () => {
    if (!dragSelection) return;
    const from = Math.min(dragSelection.startIndex, dragSelection.endIndex);
    const to = Math.max(dragSelection.startIndex, dragSelection.endIndex);
    /* A click is not a drag. Anything narrower than two points would zoom to
       a window with nothing to compare against. */
    if (to - from >= 1) {
      setZoom(windowFor(from, to));
    }
    setDragSelection(null);
  };

  const resetZoom = () => {
    setZoom(null);
    setDragSelection(null);
  };

  /* Built here rather than taken from the chart, so the legend can live
     outside the plot and not compete with the brush for room. */
  const legendPayload = uniqueProducts.map((product) => ({
    dataKey: product,
    value: product,
    color: productColors[product],
  }));

  const presetLabel = {
    today: t("today"),
    "this week": t("week"),
    "this month": t("month"),
    "this year": t("year"),
    custom: t("custom"),
  }[appliedPreset];
  /* Markers are ringed in the surface colour so crossings stay legible. */
  const surfaceColor = isDark ? "#1C3041" : "#FFFFFF";
  /* Markers shrink as the buckets get tighter, and drop out entirely once
     they would be closer together than they are wide. */
  const dotRadius = data.length <= 24 ? 4 : data.length <= 60 ? 3 : 0;
  //
  const productBody = {
    filter: {
      textSearch: "",
    },
    pageNumber: -1,
    pageSize: 0,
  };
  useEffect(() => {
  if (open) {
    setIsLoadingProducts(true);
    dispatch(getProducts(productBody))
      .catch(error => {
        console.error("Error fetching data:", error);
      })
      .finally(() => {
        setIsLoadingProducts(false);
      });
    dispatch(getSubOrdinates(employeeId, -1, 0, ""));
  }
}, [open]);
  const handleApply = () => {
    if (validateForm()) {

      const body = {
        employeeFilters: {
          productIds: Filters.productIds.length ? Filters.productIds : [0],
          employee: Filters.employee || 0,
        },
        startTime: Filters.fromDate || new Date().toISOString(),
        endTime: Filters.toDate || new Date().toISOString(),
      };
      onChangeCallEmployee(body)
      dispatch(getCallAveragEmployee(body));
      choosefilters(Filters.productIds);
      setAppliedEmployee(Filters.employee || 0);
      setAppliedPreset(selectedButton);
      handleClose();
    }
  };

  const handleReset = () => {
    setSelected({
      productIds: [],
      employee: 0,
      fromDate: moment(currentDate).subtract(7, "days").format("YYYY-MM-DD"),
      toDate: moment(currentDate).format("YYYY-MM-DD"),
    });
    setFilters({
      productIds: [],
      employee: 0,
      fromDate: moment(currentDate).subtract(7, "days").format("YYYY-MM-DD"),
      toDate: moment(currentDate).format("YYYY-MM-DD"),
    });
    setSelectedButton("this week");
  }
  //
  const validateForm = () => {
    let errors = {};
    let isValid = true;
    if (!Filters.fromDate) {
      errors.fromDate = "From date is required.";
      isValid = false;
    }
    if (!Filters.toDate) {
      errors.toDate = "To date is required.";
      isValid = false;
    }

    setFormError(errors);
    return isValid;
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);

  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const [selectedButton, setSelectedButton] = useState("this week");
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      const menuPaper = document.querySelector('.MuiMenu-paper, .MuiPopover-paper');
      
      // Don't close if clicking inside any MUI Paper (calendar, picker, dialog, etc.)
      const isInsideMuiPaper = event.target.closest('.MuiPaper-root') !== null;
      if (isInsideMuiPaper) return;
      
      if (menuPaper && !menuPaper.contains(event.target)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [open]);

  const handleToggleSelection = (event, newSelection) => {
    if (newSelection === "this week") {
      setCustomDate(false);
      const fromDate = moment().startOf("week").format("YYYY-MM-DD");
      const toDate = moment().endOf("week").format("YYYY-MM-DD");
      setSelected(prevValues => ({ ...prevValues, fromDate: fromDate, toDate: toDate }));
      setFilters(prevValues => ({ ...prevValues, fromDate: fromDate, toDate: toDate }));
    }
    else if (newSelection === "today") {
      setCustomDate(false);
      const currentDate = moment().format("YYYY-MM-DD");
      setSelected(prevValues => ({ ...prevValues, fromDate: currentDate, toDate: currentDate }));
      setFilters(prevValues => ({ ...prevValues, fromDate: currentDate, toDate: currentDate }));
    } else if (newSelection === "yesterday") {
      setCustomDate(false);
      const yesterdayDate = moment().subtract(1, "days").format("YYYY-MM-DD");
      setSelected(prevValues => ({ ...prevValues, fromDate: yesterdayDate, toDate: yesterdayDate }));
      setFilters(prevValues => ({ ...prevValues, fromDate: yesterdayDate, toDate: yesterdayDate }));
    } else if (newSelection === "last 30 days") {
      setCustomDate(false);
      const fromDate = moment().subtract(29, "days").toDate();
      const toDate = moment().toDate();
      setSelected(prevValues => ({ ...prevValues, fromDate: fromDate, toDate: toDate }));
      setFilters(prevValues => ({ ...prevValues, fromDate: fromDate, toDate: toDate }));
    } else if (newSelection === "this month") {
      setCustomDate(false);
      const fromDate = moment().startOf("month").toDate();
      const toDate = moment().endOf("month").toDate();
      setSelected(prevValues => ({ ...prevValues, fromDate: fromDate, toDate: toDate }));
      setFilters(prevValues => ({ ...prevValues, fromDate: fromDate, toDate: toDate }));
    } else if (newSelection === "last month") {
      setCustomDate(false);
      const fromDate = moment().subtract(1, "month").startOf("month").toDate();
      const toDate = moment().subtract(1, "month").endOf("month").toDate();
      setSelected(prevValues => ({ ...prevValues, fromDate: fromDate, toDate: toDate }));
      setFilters(prevValues => ({ ...prevValues, fromDate: fromDate, toDate: toDate }));
    } else if (newSelection === "this year") {
      setCustomDate(false);
      const fromDate = moment().startOf("year").toDate();
      const toDate = moment().endOf("year").toDate();
      setSelected(prevValues => ({ ...prevValues, fromDate: fromDate, toDate: toDate }));
      setFilters(prevValues => ({ ...prevValues, fromDate: fromDate, toDate: toDate }));

    } else if (newSelection === "custom") {
      setCustomDate(true);
      setSelected(prevValues => ({ ...prevValues, fromDate: "", toDate: "" }));
      setFilters(prevValues => ({ ...prevValues, fromDate: "", toDate: "" }));
    }

    setSelectedButton(newSelection);
  };
  const handleEmployeeChange = (event) => {
    setSelected(prevValues => ({
      ...prevValues,
      employee: event.value,
    }));
    setFilters(prevValues => ({
      ...prevValues,
      employee: event.value,
    }));
  };
  const getProductOptions = () => {
    if (products && products !== "isLoading" && products.result?.length) {
      return products.result
        .filter((product) => product.active)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((product) => ({
          value: product.id,
          label: product.name,
        }));
    }
    return cachedProductOptions;
  };
  const productOptions = getProductOptions();
  const isProductFieldLoading = isLoadingProducts && productOptions.length === 0;
  const getEmployeeOptions = () => {
    return (
      employees &&
      employees !== "isLoading" &&
      employees.length !== 0 &&
      employees
        .sort((a, b) => {
          const firstNameComparison = a.firstName.localeCompare(b.firstName);
          return firstNameComparison !== 0
            ? firstNameComparison
            : a.lastName.localeCompare(b.lastName);
        })
        .map((employee) => ({
          value: employee.id,
          label: `${employee.firstName} ${employee.lastName}`,
        }))
    ) || [];
  };
  const handleProductsChange = (updatedValues) => {

    setSelected(prevValues => ({
      ...prevValues,
      productIds: updatedValues,
    }));

    setFilters(prevValues => ({
      ...prevValues,
      productIds: updatedValues,
    }));
  };

  const handleFromDateChange = (date) => {
    const formattedDate = dayjs(date).format('YYYY-MM-DD');
    setSelected(prevValues => ({
      ...prevValues,
      fromDate: formattedDate,
    }));
    setFilters(prevValues => ({
      ...prevValues,
      fromDate: formattedDate,
    }));
  };
  const handleToDateChange = (date) => {
    const formattedDate = dayjs(date).format('YYYY-MM-DD');
    setSelected(prevValues => ({
      ...prevValues,
      toDate: formattedDate,
    }));
    setFilters(prevValues => ({
      ...prevValues,
      toDate: formattedDate,
    }));
  };
  return (
  <>
    {/* No blur-and-dim overlay here: the plot area swaps to a skeleton while
        it refetches, which keeps the header and filters readable and stops the
        card from jumping. */}
    <div className="chart-container line-chart-container overlay-wrapper lc-card">
      <div>
        <div>
          <div className="lc-card__header">
            <div className="lc-card__titles">
              <div className="lc-card__title-row">
                <ActivitySquare size={18} aria-hidden="true" />
                <h3 className="lc-card__title">{t("callAverageByEmployee")}</h3>
                {/* Drag-to-zoom has no visible affordance of its own, so the
                    explanation lives where "how does this work" already is,
                    rather than as another line of text on the card. */}
                <MaterialTooltip
                  title={`${t("callAverageHelp")} ${t("dragToZoom")}.`}
                  arrow
                  enterTouchDelay={0}
                  placement="top"
                >
                  <span className="lc-card__info" tabIndex={0} role="note">
                    <Info size={14} aria-hidden="true" />
                  </span>
                </MaterialTooltip>
              </div>
              {/* Says in words what a point on this chart means - the title
                  alone has never been enough for people to read it right. */}
              <p className="lc-card__subtitle">{t("callsOverTime")}</p>
            </div>
            <div className="lc-card__filter-wrap">
              <MaterialTooltip title={t("applyFilters")} arrow enterDelay={500} placement="bottom">
                <button
                  type="button"
                  className={`lc-card__filter-btn${open ? " is-active" : ""}`}
                  onClick={handleClick}
                  aria-label={t("applyFilters")}
                  aria-haspopup="true"
                  aria-expanded={open}
                >
                  <FilterAltOutlinedIcon fontSize="small" />
                </button>
              </MaterialTooltip>
              {hasActiveFilters && <span className="lc-card__filter-dot" />}
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  style: {
                    width: "640px",
                    borderRadius: "4px",
                    background: "#FFFFFF",
                    boxShadow: "0px 11px 15px -7px #00000033",
                  },
                }}
              >
                <div className="row" style={{
                  padding: "16px 24px 16px 24px",
                  justify: "space-between",
                  display: "flex",
                  gap: "24px"
                }}>
                  <div className="col-12 d-flex ml-2">
                    <BodyMediumRegular text={t("applyFilters")} />
                    <MaterialTooltip title="Close Filter" arrow enterDelay={500}>
                      <CloseIcon style={{ marginLeft: "auto", cursor: "pointer" }} onClick={handleClose} />
                    </MaterialTooltip>
                  </div>
                </div>
                <div className="row" style={{
                  gap: "24px",
                  borderBottom: "1px solid #0000001F",
                  padding: "24px 16px 24px 16px",
                  alignItems: "center"
                }}>
                  <div className="row" style={{ gap: "16px" }}>
                    <div className="col-12 d-flex justify-content-center align-items-center" style={{ height: "48px" }}>
                      <ToggleButtonGroup
                        exclusive
                        value={selectedButton}
                        onChange={handleToggleSelection}
                        aria-label="button group"
                        style={{
                          width: "100%",
                          height: "40px",
                          gap: "12px",
                          borderRadius: "6px",
                          padding: "4px 8px",
                          backgroundColor: "#f9fafb",
                          display: "flex",
                          justifyContent: "space-evenly",
                          alignItems: "center",
                        }}
                      >

                        {["today", "this week", "this month", "this year", "custom"].map((value) => (
                          <ToggleButton
                            key={value}
                            value={value}
                            style={{
                              minWidth: "34px",
                              height: "34px",
                              padding: "4px 12px",
                              borderRadius: "6px",
                              textTransform: "none",
                              border: "none",
                              backgroundColor: selectedButton === value ? "#FFFFFF" : "inherit",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <BodyMediumRegular text={buttonLabels[value]} />
                          </ToggleButton>
                        ))}

                      </ToggleButtonGroup>
                    </div>
                    <div className="col-12 d-flex" style={{
                      height: "56px",
                    }}>
                      <div className="col-6">
                        <div style={{ marginBottom: "6px" }}>
                          <BodySmallRegular text={t("from")} />
                        </div>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker

                            valueType="date"
                            value={selected.fromDate && selected.fromDate.length != 0 ? dayjs(selected.fromDate) : null}
                            onChange={handleFromDateChange}
                            disabled={!customDate}
                            renderInput={(params) => <TextField fullWidth {...params} />}
                            sx={{
                              width: "100%",
                              "& .MuiOutlinedInput-root": {
                                height: "40px",
                                padding: "0",
                                boxSizing: "border-box",
                                backgroundColor: "transparent",
                                border: "1px solid #E0E0E0",
                                borderRadius: "60px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                              },
                              "& .MuiInputBase-input": {
                                height: "32px",
                                padding: "0px 8px",
                                fontSize: "14px",
                              },
                              "& .MuiIconButton-root": {
                                padding: "20px",
                              },
                            }}
                          />
                        </LocalizationProvider>
                        {formError.fromDate && (
                          <span style={{ color: "red" }}>{formError.fromDate}</span>
                        )}
                      </div>
                      <div className="col-6">
                        <div style={{ marginBottom: "6px" }}>
                          <BodySmallRegular text={t("to")} />
                        </div>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>

                          <DatePicker

                            valueType="date"
                            value={selected.toDate && selected.toDate.length != 0 ? dayjs(selected.toDate) : null}
                            onChange={handleToDateChange}
                            disabled={!customDate}
                            renderInput={(params) => <TextField fullWidth {...params} />}
                            sx={{
                              width: "100%",
                              "& .MuiOutlinedInput-root": {
                                height: "40px",
                                padding: "0",
                                boxSizing: "border-box",
                                backgroundColor: "transparent",
                                border: "1px solid #E0E0E0",
                                borderRadius: "60px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                              },
                              "& .MuiInputBase-input": {
                                height: "32px",
                                padding: "0px 8px",
                                fontSize: "14px",
                              },
                              "& .MuiIconButton-root": {
                                padding: "20px",
                              },
                            }}
                          />
                        </LocalizationProvider>
                        {formError.toDate && (
                          <span style={{ color: "red" }}>{formError.toDate}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row" style={{ gap: "16px" }}>
                    <div className="col-12 d-flex" >
                      <div className="col-6">
                        <MultiSelect
                          isSearchable
                          onChange={handleProductsChange}
                          value={selected?.productIds || []}
                          label={t("product")}
                          options={productOptions}
                          isLoading={isProductFieldLoading}
                          placeholder={isProductFieldLoading ? "Loading Products..." : t("select")}
                        />
                      </div>
                      <div className="col-6">
                        <SingleSelect
                          label={t("employee")}
                          isSearchable
                          secondaryLabel
                          iconEnd
                          onChange={handleEmployeeChange}
                          value={selected.employee}
                          placeholder={t("employee")}
                          options={getEmployeeOptions()}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row mr-4" style={{
                  padding: "16px 24px 16px 24px",
                  gap: "8px",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  flexWrap: "nowrap"
                }}>
                 <button
                type="submit"
                className="primary-button"
                disabled={false}
                onClick={handleApply}
                  style={{ width: "78px", minWidth: "78px", maxWidth: "78px", flex: "0 0 78px" }}
              >
                <span className="button-content">
                  <p className="button-text save">{t("apply")}</p>
                </span>
              </button>
              <button
                className="cancel-button"
                type="button"
                onClick={handleReset}
                style={{ width: "88px", minWidth: "88px", maxWidth: "88px", flex: "0 0 88px" }}

              >
                <span className="button-content">
                 <p className="button-text">{t("reset")}</p>
                </span>
              </button>
                </div>

              </Menu>
            </div>
          </div>

          {/* What is on screen right now: who, over which dates, across how
              many products. Without this row there was no way to tell what a
              filtered chart was showing. */}
          <div className="lc-scope">
            {shownEmployeeName && (
              <span className="lc-scope__person">
                <User size={15} aria-hidden="true" />
                {shownEmployeeName}
              </span>
            )}
            {rangeLabel && (
              <span className="lc-chip">
                <CalendarDays aria-hidden="true" />
                {rangeLabel}
                {presetLabel && (
                  <span className="lc-chip__preset">{presetLabel}</span>
                )}
              </span>
            )}
            <span className="lc-chip">
              <Package aria-hidden="true" />
              {productScopeLabel}
            </span>
            {hasPlottedData && (
              <>
                <GranularitySelect
                  value={granularity}
                  options={GRANULARITY_OPTIONS}
                  resolvedLabel={granularityLabel}
                  autoLabel={t("auto")}
                  ariaLabel={t("granularity")}
                  onChange={setGranularity}
                />
              </>
            )}
            {isZoomed && (
              <button
                type="button"
                className="lc-chip lc-chip--button lc-chip--active"
                onClick={resetZoom}
              >
                <Search aria-hidden="true" />
                {t("zoomedIn")}
                <X className="lc-chip__caret" aria-hidden="true" />
              </button>
            )}
          </div>

          {/* Counted off the raw days the API returned, so the figures
              describe the employee rather than the resolution the chart
              happens to be drawn at. */}
          {hasPlottedData && (
            <div className="lc-stats">
              <div className="lc-stat">
                <span className="lc-stat__value">{summary.totalCalls}</span>
                <span className="lc-stat__label">{t("totalCalls")}</span>
              </div>
              <div className="lc-stat">
                <span className="lc-stat__value">{summary.activeDays}</span>
                <span className="lc-stat__label">{t("activeDays")}</span>
              </div>
            </div>
          )}
        </div>


        <div className="graph-container">
          {isChartLoading && <div className="lc-skeleton" aria-hidden="true" />}

          {hasPlottedData && (
            <div className="lc-plot">
              <ResponsiveContainer width="100%" height="100%">
                {/* A line per product. What makes it readable is not the mark
                    but what feeds it: contiguous time buckets, so the spacing
                    is real, and a window the reader can narrow by dragging
                    across the plot. */}
                <LineChart
                  data={data}
                  margin={{ top: 16, right: 16, left: 0, bottom: 4 }}
                  throttleDelay={80}
                  onMouseDown={handleZoomStart}
                  onMouseMove={handleZoomMove}
                  onMouseUp={handleZoomEnd}
                  onMouseLeave={handleZoomEnd}
                  style={{ cursor: dragSelection ? "col-resize" : "crosshair" }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    dataKey="date"
                    minTickGap={16}
                    interval="preserveStartEnd"
                  />
                  {/* The chart had no y axis at all, so a peak of 3 and a peak
                      of 300 looked identical. */}
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    width={44}
                    allowDecimals={false}
                    tickMargin={4}
                  >
                    <Label
                      value={t("calls")}
                      angle={-90}
                      position="insideLeft"
                      className="recharts-axis-label"
                      style={{ textAnchor: "middle" }}
                    />
                  </YAxis>
                  <Tooltip
                    content={<LineChartTooltip />}
                    cursor={{ strokeDasharray: "4 4" }}
                    active={dragSelection ? false : undefined}
                  />
                  {uniqueProducts.map((product) => (
                    <Line
                      key={product}
                      type="monotone"
                      dataKey={product}
                      name={product}
                      hide={hiddenSeries.includes(product)}
                      stroke={productColors[product]}
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      /* Past the eighth product the palette reuses its hues,
                         so the repeats are dashed - two products never share
                         both a colour and a stroke. */
                      strokeDasharray={getSeriesDash(product, "products")}
                      /* A ring in the surface colour keeps the markers
                         readable where two series cross or sit on zero
                         together. */
                      dot={
                        dotRadius
                          ? {
                              r: dotRadius,
                              fill: productColors[product],
                              stroke: surfaceColor,
                              strokeWidth: 2,
                            }
                          : false
                      }
                      activeDot={{
                        r: dotRadius ? dotRadius + 2 : 5,
                        stroke: surfaceColor,
                        strokeWidth: 2,
                      }}
                      isAnimationActive={false}
                    />
                  ))}

                  {/* Shades the stretch being dragged over, so the selection
                      is visible before it is committed. */}
                  {dragSelection && dragSelection.left !== dragSelection.right && (
                    <ReferenceArea
                      x1={dragSelection.left}
                      x2={dragSelection.right}
                      strokeOpacity={0}
                      fill={isDark ? "#91A9BE" : "#1E293B"}
                      fillOpacity={0.12}
                    />
                  )}

                  {/* The brush doubles as the overview: it always shows the
                      whole range, with the visible window drawn inside it, so
                      a zoomed-in reader can still see where they are. */}
                  {data.length > 2 && (
                    <Brush
                      dataKey="date"
                      height={24}
                      travellerWidth={8}
                      stroke={isDark ? "#91A9BE" : "#94A3B8"}
                      fill={isDark ? "#233C52" : "#F9FAFB"}
                      startIndex={zoomWindow ? zoomWindow.startIndex : undefined}
                      endIndex={zoomWindow ? zoomWindow.endIndex : undefined}
                      onChange={(range) => {
                        if (!range || range.startIndex == null) return;
                        /* Ignore anything that does not fit the data on
                           screen, so a stale callback cannot shrink it. */
                        if (range.endIndex > data.length - 1) return;
                        setZoom(windowFor(range.startIndex, range.endIndex));
                      }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {hasPlottedData && uniqueProducts.length > 1 && (
            <LineChartLegend
              payload={legendPayload}
              hidden={hiddenSeries}
              onToggle={toggleSeries}
            />
          )}

          {!isChartLoading && data.length === 0 && (
            <div className="lc-empty">
              <span className="lc-empty__icon">
                <ActivitySquare size={20} aria-hidden="true" />
              </span>
              <p className="lc-empty__title">{t("noCallsTitle")}</p>
              <p className="lc-empty__hint">{t("noCallsHint")}</p>
            </div>
          )}
        </div>
      </div>
          </div>

    </>
  );
}
